import React, { useEffect, useMemo, useState } from "react";
import { useNodeOptions } from "../../hooks/useNodeOptions";
import { resolveDependencyKey } from "../../utils/dynamicNodeData";
import { capabilityBlocked, optionLabel, optionSources } from "../../utils/nodeBuilder";

// Catalog-driven remote pickers for the dynamic node.
//
// - `client-key-picker` lists stored client API keys (never the secret) through the endpoint the
//   catalog names, filtered by `control_options.filter.service` when given.
// - `remote-select` renders every `control_options.sources` entry (e.g. Slack Channels/People) and
//   refetches when one of its `depends_on` fields changes.

const fieldStyle = {
  width: "100%",
  padding: "5px 8px",
  fontSize: "12px",
  fontFamily: "inherit",
  color: "var(--main-font-color)",
  backgroundColor: "var(--main-bg)",
  border: "1px solid var(--main-border-color)",
  borderRadius: "5px",
  boxSizing: "border-box",
};

const hintStyle = { fontSize: "10.5px", fontStyle: "italic", color: "#a0a6b1" };
const errorStyle = { fontSize: "10.5px", color: "#e74c3c" };

const sourceTabStyle = (active) => ({
  padding: "2px 8px",
  fontSize: "10.5px",
  fontWeight: 600,
  border: "1px solid var(--main-border-color)",
  borderRadius: "999px",
  backgroundColor: active ? "var(--sidebar-active-item-bg)" : "transparent",
  color: "var(--main-font-color)",
  cursor: "pointer",
});

const smallButtonStyle = {
  padding: "2px 7px",
  fontSize: "10.5px",
  border: "1px solid var(--main-border-color)",
  borderRadius: "5px",
  backgroundColor: "transparent",
  color: "var(--main-font-color)",
  cursor: "pointer",
};

/** A select that mirrors its value locally so the choice survives the node-data round-trip. */
const LatchedSelect = ({ value, options, onChange, placeholder, disabled = false }) => {
  const stringValue = value === undefined || value === null ? "" : String(value);

  const [selected, setSelected] = useState(stringValue);
  useEffect(() => {
    setSelected(stringValue);
  }, [stringValue]);

  const hasCurrent = options.some((option) => String(option.value) === selected);
  const finalOptions = hasCurrent || !selected ? options : [{ value: selected, label: selected }, ...options];

  return (
    <select
      className="nodrag"
      style={{ ...fieldStyle, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
      value={selected}
      disabled={disabled}
      onChange={(event) => {
        setSelected(event.target.value);
        onChange(event.target.value);
      }}
    >
      <option value="">{placeholder}</option>
      {finalOptions.map((option) => (
        <option key={String(option.value)} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

/**
 * A missing-scope response is actionable: show the message, the scopes that are needed and the
 * ones already provided, instead of a generic "request failed".
 */
const ScopeError = ({ detail }) => {
  if (!detail) return null;

  const needed = Array.isArray(detail.needed) ? detail.needed : [];
  const provided = Array.isArray(detail.provided) ? detail.provided : [];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        padding: "6px 8px",
        fontSize: "10.5px",
        lineHeight: 1.4,
        border: "1px solid #f39c12",
        borderRadius: "6px",
        backgroundColor: "rgba(243, 156, 18, 0.12)",
        color: "var(--main-font-color)",
      }}
    >
      <strong>{detail.slack_error === "missing_scope" ? "Missing scope" : "Connection error"}</strong>
      <span>{detail.message}</span>
      {needed.length > 0 ? <span>Needed: {needed.join(", ")}</span> : null}
      {provided.length > 0 ? <span>Provided: {provided.join(", ")}</span> : null}
      {detail.code ? <span style={{ opacity: 0.7 }}>{detail.code}</span> : null}
    </div>
  );
};

/**
 * `control: "client-key-picker"` — a stored API key, filtered by service when the catalog says so.
 */
export const ClientKeyOptionsPicker = ({ parameter, value, onChange, optionsEndpoint = null }) => {
  const controlOptions = parameter?.control_options ?? {};
  const source = controlOptions.source ?? "client-api-keys";
  const service = controlOptions.filter?.service ?? controlOptions.service ?? null;

  const { options, loading, error, errorDetail } = useNodeOptions({
    controlOptions: controlOptions.endpoint || controlOptions.request
      ? controlOptions
      : { ...controlOptions, endpoint: optionsEndpoint },
    source,
    data: {},
  });

  const normalized = useMemo(
    () => options.map((option) => ({ value: option.value, label: option.label ?? option.value })),
    [options]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "3px", width: "100%" }}>
      <LatchedSelect
        value={value}
        options={normalized}
        onChange={onChange}
        disabled={loading && normalized.length === 0}
        placeholder={loading ? "Loading keys…" : "Select a client API key"}
      />

      {errorDetail ? <ScopeError detail={errorDetail} /> : null}
      {!errorDetail && error ? <span style={errorStyle}>{error}</span> : null}

      {!error && !loading && normalized.length === 0 ? (
        <span style={hintStyle}>No {service ? `${service} ` : ""}keys saved for this client.</span>
      ) : null}
    </div>
  );
};

/**
 * `control: "remote-select"` — one or more remote option sources, refetched when any `depends_on`
 * field changes.
 */
export const RemoteSelectControl = ({ parameter, data = {}, value, onChange, disabled = false, capabilities = null, optionsEndpoint = null }) => {
  const controlOptions = parameter?.control_options;
  const searchable = controlOptions?.searchable !== false;
  const dependsOn = Array.isArray(controlOptions?.depends_on) ? controlOptions.depends_on : [];

  const sources = useMemo(() => optionSources(controlOptions ?? {}), [controlOptions]);

  const [activeSource, setActiveSource] = useState(sources[0]?.source ?? null);

  useEffect(() => {
    if (!sources.some((entry) => entry.source === activeSource)) {
      setActiveSource(sources[0]?.source ?? null);
    }
  }, [sources, activeSource]);

  const active = sources.find((entry) => entry.source === activeSource) ?? sources[0] ?? null;

  const key = resolveDependencyKey(controlOptions ?? {}, data);
  const enabled = Boolean(active) && (dependsOn.length === 0 || Boolean(key)) && !disabled;

  const { options, nextCursor, loading, error, errorDetail, loadMore } = useNodeOptions({
    controlOptions: controlOptions.endpoint || controlOptions.request
      ? controlOptions
      : { ...controlOptions, endpoint: optionsEndpoint },
    source: active?.source,
    data,
    enabled,
  });

  const [search, setSearch] = useState("");

  const normalized = useMemo(
    () =>
      options.map((option) => ({
        value: option.value,
        label: optionLabel(option, active ?? {}),
      })),
    [options, active]
  );

  const filtered = search.trim()
    ? normalized.filter((option) =>
        `${option.label} ${option.value}`.toLowerCase().includes(search.trim().toLowerCase())
      )
    : normalized;

  if (!active) return <span style={hintStyle}>No option source configured.</span>;

  if (dependsOn.length > 0 && !key) {
    return <span style={hintStyle}>Choose a client key first.</span>;
  }

  if (disabled) {
    return <span style={hintStyle}>Unavailable for this connection.</span>;
  }

  // Disable a source the connection cannot read (e.g. channels without channels:read).
  const tail = String(active.source ?? "").split(".").pop();
  const blocked = capabilityBlocked(capabilities, [tail, `list_${tail}`]) === true;

  if (blocked) {
    return <span style={hintStyle}>Missing scopes to list {active.label.toLowerCase()}.</span>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}>
      {sources.length > 1 ? (
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
          {sources.map((entry) => (
            <button
              key={entry.source}
              type="button"
              className="nodrag"
              style={sourceTabStyle(entry.source === active.source)}
              onClick={() => setActiveSource(entry.source)}
            >
              {entry.label}
            </button>
          ))}
        </div>
      ) : null}

      {searchable ? (
        <input
          className="nodrag"
          type="text"
          style={fieldStyle}
          value={search}
          placeholder="Search…"
          onChange={(event) => setSearch(event.target.value)}
        />
      ) : null}

      <LatchedSelect
        value={value}
        options={filtered}
        onChange={onChange}
        disabled={loading && filtered.length === 0}
        placeholder={loading ? "Loading…" : `Select ${active.label.toLowerCase()}…`}
      />

      {errorDetail ? <ScopeError detail={errorDetail} /> : null}
      {!errorDetail && error ? <span style={errorStyle}>{error}</span> : null}
      {!error && !loading && normalized.length === 0 ? (
        <span style={hintStyle}>No {active.label.toLowerCase()} available.</span>
      ) : null}

      {nextCursor ? (
        <button type="button" className="nodrag" style={smallButtonStyle} onClick={loadMore} disabled={loading}>
          {loading ? "Loading…" : "Load more"}
        </button>
      ) : null}
    </div>
  );
};

export default ClientKeyOptionsPicker;
